import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {LineChart, LineChartColor, LineChartItem} from "../../core/components/charts/lineChart";
import {Localizer} from "../../infrastructure/localization/localizer";
import {RoutePaths} from "../../infrastructure/routes";
import {NotificationModule} from "ditmer-embla";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {forloebReducer, ForloebSliceState} from "../../core/forloebSlice";
import {ModalAcceptType, ModalSubmitMessage} from "../../core/components/modalMessage/modalSubmitMessage";
import {KompetenceEvalueringSektion} from "./sektion/kompetenceEvalueringSektion";
import {ScoreTypeEnum} from "../../core/componentsPage/score/scoreTypeEnum";
import {EvalueringCollapse} from "../../core/components/Tabs/evalueringCollapse";
import classNames from "classnames";
import useScreenResolution from "../../core/hooks/useScreenResolution";
import useLogbogApi from "../../core/hooks/useLogbogApi";
import {KompetenceEvalueringHookType} from "./useKompetenceEvaluering";
import {findIndexOrUndefined} from "../../core/helpers/filterHelper";
import {KompetenceSpoergsmaalModel,} from "../../core/sharedmodels/evaluering/kompetenceSpoergsmaalModel";
import {ScoreModel} from "../../core/sharedmodels/evaluering/scoreModel";
import {SpoergsmaalType} from "../../core/sharedmodels/evaluering/spoergsmaalType";
import {KompetenceSektionModel} from "../../core/sharedmodels/evaluering/kompetenceSektionModel";
import {KompetenceSvarModel} from "../../core/sharedmodels/evaluering/KompetencesvarModel";
import useUser from "../../core/hooks/useUser";
import {setActiveKompetencePageTab} from "../kompetencerPage/cards/kompetenceCardSlice";

type KompetenceEvalueringProps = {
    hook: KompetenceEvalueringHookType;
};

const getDefaultValues = (hook: KompetenceEvalueringHookType) => {
    const anyAnsweredQuestions = hook.evalueringSections.findIndex(s => s.spoergsmaal.find(spoergsmaal => hook.questionHasAnswer(spoergsmaal))) > 0;
    const firstSektionWithUnansweredQuestion = findIndexOrUndefined(hook.evalueringSections, s => !!s.spoergsmaal.length && s.includedInProgress && !s.spoergsmaal.every(spoergsmaal => hook.questionHasAnswer(spoergsmaal)));

    const openSectionIndex = !anyAnsweredQuestions ? 0 : firstSektionWithUnansweredQuestion ? firstSektionWithUnansweredQuestion : hook.evalueringSections.length - 1;
    const openTabId = hook.evalueringSections[openSectionIndex]?.id ?? undefined;

    return {
        openSectionIndex,
        openTabId
    }
}

export function KompetenceEvaluering({hook}: KompetenceEvalueringProps) {
    const dispatch = useAppDispatch();
    const godkendModalId = "kompetenceEvalueringGodkend";
    const {currentUser} = useUser()

    const history = useHistory();
    const {isMobile} = useScreenResolution();
    const {evalueringApi} = useLogbogApi();

    const forloebSliceState = useAppSelector(forloebReducer) as ForloebSliceState;

    const [showGodkendModal, setShowGodkendModal] = useState(false);
    const [sektionIndexState, setSektionIndexState] = useState<number>(getDefaultValues(hook).openSectionIndex);
    const [isIndsendState, setIsIndsendState] = useState(getDefaultValues(hook).openSectionIndex === hook.evalueringSections.length - 1);
    const [expandedTabId, setExpandedTabId] = useState<string>(getDefaultValues(hook).openTabId);

    const generateChartItems = (): LineChartItem[] => {
        return [
            {count: sektionIndexState ?? 0, color: LineChartColor.success},
            {count: (hook.evalueringSections?.length - 1) - (sektionIndexState ?? 0), color: LineChartColor.gray}
        ];
    }

    const indsendEvaluering = async () => {
        if (!hook.kompetenceEvaluering) return;
        hook.setIndsendtStatus(true);
        const indsendtId = await evalueringApi.patchlaegeKompetenceEvaluering(hook.kompetenceEvaluering.laegekompetenceEvalueringId, true, []);
        if (indsendtId) {
            NotificationModule.showSuccess(Localizer.kompetenceEvalueringPage_evalueringGemt(), "");
            history.push(RoutePaths.Kompetencer({
                uddannelseslaege: forloebSliceState.forloebState.brugerId,
            }).url)
        }
    }

    useEffect(() => {
        //On dismount set active-tab to "vurderinger".
        return () => {
            dispatch(setActiveKompetencePageTab(Localizer.global_evaluations()));
        }
    }, [dispatch]);

    const handleOpenSection = (sektionIndexState: number, id: string) => {
        setSektionIndexState(sektionIndexState);
        setIsIndsendState(sektionIndexState === hook.evalueringSections.length - 1);
        setExpandedTabId(id)
    }

    const GetSpoergsmaalScore = (spoergsmaal: KompetenceSpoergsmaalModel[]): ScoreModel => {
        const firstQuestion = spoergsmaal[0];
        const singleAnswer = firstQuestion?.evalueringSvar[0];
        const singleSvarmulighed = firstQuestion?.svarmuligheder.find(x => x.id === singleAnswer?.svarmulighedId);
        const multipleQuestionsInSection = spoergsmaal.length > 1;
        const multipleAnswersInSection = firstQuestion?.antalSvar > 1 || multipleQuestionsInSection;

        return {
            scoreValue: (!multipleAnswersInSection && !singleSvarmulighed?.udeladSvarvaerdi) ? singleSvarmulighed?.svarVaerdi : undefined,
            notRelevant: singleAnswer?.ikkeRelevant ?? false,
            farvekode: singleSvarmulighed?.farvekode,
            svarVaerdiUdeladt: singleSvarmulighed?.udeladSvarvaerdi,
            allQuestionsHasAtleastOneAnswer: multipleAnswersInSection && spoergsmaal.filter(x => x.isRequired && !x.isFeedback).every(x => x.evalueringSvar.length > 0) && spoergsmaal.some(s => s.evalueringSvar.length > 0),
        };
    }

    const otherQuestionsHasAnswers = (currentSpoergsmaalId: string, section: KompetenceSektionModel): boolean => {
        const datoAndGodkendBothAnsweredForOtherQuestions = (spoergsmaal: KompetenceSpoergsmaalModel) => {
            if (spoergsmaal.spoergsmaalTypeEnum !== SpoergsmaalType.DatoOgGodkendt) return true;
            return spoergsmaal.evalueringSvar.every(es => !!es.dateSvar && es?.booleanSvar !== undefined);
        }

        return section.spoergsmaal
            .filter(s => s.id !== currentSpoergsmaalId)
            .every(s => s.evalueringSvar.length === s.antalSvar && datoAndGodkendBothAnsweredForOtherQuestions(s));
    }

    const currentQuestionsHasAnswers = (section: KompetenceSektionModel, newSvar: KompetenceSvarModel): boolean => {
        const currentSpoergsmaal = section.spoergsmaal.find(s => s.id === newSvar.spoergsmaalId);
        const datoAndGodkendBothAnsweredForCurrentQuestions = (spoergsmaal: KompetenceSpoergsmaalModel) => {
            if (spoergsmaal.spoergsmaalTypeEnum !== SpoergsmaalType.DatoOgGodkendt) return true;
            return !!newSvar.dateSvar && newSvar?.booleanSvar !== undefined;
        }

        //+1 fordi det nuværende spørgsmåls antal svar ikke er opdateret endnu, grundet updateSvarCallback ikke er eksekveret endnu - kan refaktureres til at lytte på statet.
        if (currentSpoergsmaal)
            return currentSpoergsmaal?.evalueringSvar && (currentSpoergsmaal?.evalueringSvar?.length + 1) >= currentSpoergsmaal?.antalSvar && datoAndGodkendBothAnsweredForCurrentQuestions(currentSpoergsmaal);
        else return false;
    }

    const nextSectionExists = (index: number) => {
        return hook.evalueringSections[index + 1]?.id;
    }

    const showRedBorder = (sektion: KompetenceSektionModel) => {
        //Er brugeren ikke læge og sektionen har spørgsmål som er required og ikke er besvarelse:
        return isIndsendState && !currentUser.IsLaege() && sektion.spoergsmaal.some(x => x.isRequired && x.evalueringSvar.length < 1);
    }

    return (
        <>

            <div className="card shadow-none sticky-top">
                <div className="card-header justify-content-center">
                    <div className={classNames("w-75 text-center", isMobile ? "padding-m" : "padding-l")}>
                        <LineChart items={generateChartItems()} hideLabels={true}/>
                        <p>{sektionIndexState + 1}/{hook.evalueringSections.length}</p>
                    </div>
                </div>
            </div>
            {hook.evalueringSections.sortBy(x => x.sortOrder).map((sektion, index) => (
                <div className={"card card-small-margin margin-bottom-s shadow-sm"} key={sektion.id}>
                    <EvalueringCollapse additionalClasses={classNames("flex-row", showRedBorder(sektion) && " border border-danger")}
                                        id={sektion.id}
                                        title={sektion.title}
                                        expanded={expandedTabId === sektion.id}
                                        toggleCallback={(id: string) => {
                                            handleOpenSection(index, id);
                                        }}
                                        score={GetSpoergsmaalScore(sektion.spoergsmaal)}
                                        feedbackSpoergsmaal={sektion.spoergsmaal.some(s => s.isFeedback) ? sektion.spoergsmaal.filter(x => x.isFeedback) : undefined}
                                        scoreType={ScoreTypeEnum.KompetenceEvalueringScore}>
                        <KompetenceEvalueringSektion
                            sektion={sektion}
                            requiredAndPassed={index > sektionIndexState}
                            answerUpdateLoading={hook.answerUpdateLoading}
                            updateSvarCallback={(newSvar: KompetenceSvarModel) => hook.updateSvar(newSvar)
                                .then(() => {
                                    const updatedSektion = hook.evalueringSections.find(x => x.id === sektion.id);
                                    if (updatedSektion && nextSectionExists(index) && otherQuestionsHasAnswers(newSvar.spoergsmaalId, updatedSektion) && currentQuestionsHasAnswers(updatedSektion, newSvar))
                                        handleOpenSection(index + 1, nextSectionExists(index))
                                })}
                            deleteSvarCallback={hook.deleteSvar}
                            kompetenceEvalueringId={hook.kompetenceEvaluering.laegekompetenceEvalueringId}
                            evalueringIndsendt={hook.kompetenceEvaluering?.indsendt ?? false}
                            finishSectionDisable={currentUser.IsLaege() || hook.evalueringSections.flatMap(x => x.spoergsmaal).filter(x => x.isRequired).some(x => x.evalueringSvar.length < 1)}
                            setShowGodkendModalCallback={(show: boolean) => setShowGodkendModal(show)}
                            finishSektion={isIndsendState}
                            nextButtonClickedCallback={() => {
                                handleOpenSection(index + 1, hook.evalueringSections[index + 1].id)
                            }}
                        />
                    </EvalueringCollapse>
                </div>
            ))}

            {showGodkendModal &&
                <ModalSubmitMessage
                    title={Localizer.kompetenceEvalueringPage_saveVurderingAfKompetence()}
                    description={<p>{Localizer.KompetenceEvalueringPage_evalueringIndsendApprove()}</p>}
                    modalAcceptType={ModalAcceptType.primary}
                    modalId={godkendModalId}
                    acceptAction={indsendEvaluering}
                    cancelAction={() => setShowGodkendModal(false)}
                    acceptButtonText={Localizer.global_save()}
                />
            }
        </>
    );
}
